import { useState, useEffect } from 'react'
import type { LabelTemplateResponse, LabelVariable } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
  LabelPreview,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'
import { downloadLabelTemplate } from './label-template-download'

// Paper size datalist options per driver
const DYMO_PAPER_SIZES = ['dymo_30252_address', 'dymo_30334_multi', 'dymo_99012', 'dymo_30336']
const ZEBRA_PAPER_SIZES = ['zebra_2x1', 'zebra_3x1', 'zebra_4x1', 'zebra_4x2', 'zebra_4x6']

interface TemplateForm {
  name: string
  description: string
  driver: 'dymo_client' | 'zebra_browser_print'
  paperSize: string
  templateBody: string
  variables: LabelVariable[]
}

const defaults: TemplateForm = {
  name: '',
  description: '',
  driver: 'dymo_client',
  paperSize: '',
  templateBody: '',
  variables: [],
}

interface Props {
  readonly templateId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (t: LabelTemplateResponse) => void
  readonly onArchived?: (id: string) => void
}

function templateToForm(t: LabelTemplateResponse): TemplateForm {
  return {
    name: t.name,
    description: t.description ?? '',
    driver: t.driver,
    paperSize: t.paperSize ?? '',
    templateBody: t.templateBody,
    variables: t.variables.map((v) => ({ ...v })),
  }
}

const VARIABLE_TYPE_OPTIONS = ['string', 'number', 'date', 'barcode'] as const

interface VariableRowProps {
  readonly variable: LabelVariable
  readonly index: number
  readonly onChange: (index: number, updated: LabelVariable) => void
  readonly onRemove: (index: number) => void
}

function VariableRow({ variable, index, onChange, onRemove }: VariableRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 1fr 1.5fr 0.5fr 1fr auto',
        gap: '0.5rem',
        alignItems: 'center',
        marginBottom: '0.5rem',
      }}
    >
      <input
        style={input}
        placeholder="name"
        value={variable.name}
        onChange={(e) => onChange(index, { ...variable, name: e.target.value })}
        aria-label={`Variable ${index + 1} name`}
      />
      <select
        style={input}
        value={variable.type}
        onChange={(e) => onChange(index, { ...variable, type: e.target.value as LabelVariable['type'] })}
        aria-label={`Variable ${index + 1} type`}
      >
        {VARIABLE_TYPE_OPTIONS.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <input
        style={input}
        placeholder="label"
        value={variable.label}
        onChange={(e) => onChange(index, { ...variable, label: e.target.value })}
        aria-label={`Variable ${index + 1} label`}
      />
      <label
        style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
      >
        <input
          type="checkbox"
          checked={variable.required}
          onChange={(e) => onChange(index, { ...variable, required: e.target.checked })}
          aria-label={`Variable ${index + 1} required`}
        />
        Req
      </label>
      <input
        style={input}
        placeholder="example"
        value={variable.example ?? ''}
        onChange={(e) => onChange(index, { ...variable, example: e.target.value || undefined })}
        aria-label={`Variable ${index + 1} example`}
      />
      <button
        type="button"
        style={{ ...btnDanger, padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
        onClick={() => onRemove(index)}
        aria-label={`Remove variable ${index + 1}`}
      >
        ✕
      </button>
    </div>
  )
}

interface PreviewState {
  body: string | null
  variables: Record<string, string>
  error: string | null
  loading: boolean
  noOrders: boolean
}

const emptyPreview: PreviewState = { body: null, variables: {}, error: null, loading: false, noOrders: false }

export function LabelTemplateEditModal({ templateId, isOpen, isNew, onClose, onSaved, onArchived }: Props) {
  const [form, setForm] = useState<TemplateForm>(defaults)
  const [currentTemplate, setCurrentTemplate] = useState<LabelTemplateResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewState>(emptyPreview)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [showLivePreview, setShowLivePreview] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setCurrentTemplate(null)
      setError(null)
      setPreview(emptyPreview)
      return
    }
    if (!templateId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setPreview(emptyPreview)
    api
      .get<LabelTemplateResponse>(`/admin/label-templates/${templateId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) {
          setForm(templateToForm(res.data))
          setCurrentTemplate(res.data)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load template')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [templateId, isOpen, isNew])

  const update = <K extends keyof TemplateForm>(key: K, value: TemplateForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  function handleVariableChange(index: number, updated: LabelVariable) {
    setForm((prev) => {
      const vars = [...prev.variables]
      vars[index] = updated
      return { ...prev, variables: vars }
    })
  }

  function handleVariableRemove(index: number) {
    setForm((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index),
    }))
  }

  function addVariable() {
    const newVar: LabelVariable = { name: '', type: 'string', label: '', required: false }
    setForm((prev) => ({ ...prev, variables: [...prev.variables, newVar] }))
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        driver: form.driver,
        templateBody: form.templateBody,
        variables: form.variables,
      }
      if (form.description) payload.description = form.description
      if (form.paperSize) payload.paperSize = form.paperSize

      const res = isNew
        ? await api.post<LabelTemplateResponse>('/admin/label-templates', payload)
        : await api.put<LabelTemplateResponse>(`/admin/label-templates/${templateId}`, payload)
      if (!res.data) throw new Error('No data returned')
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!templateId) return
    setArchiving(true)
    try {
      await api.post(`/admin/label-templates/${templateId}/archive`, {})
      onArchived?.(templateId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  async function handleRenderPreview() {
    if (!templateId || isNew) return
    setPreview({ body: null, variables: {}, error: null, loading: true, noOrders: false })
    try {
      // Fetch first order in the org
      const ordersRes = await api.get<readonly { id: string }[]>('/orders?limit=1&page=1')
      const orders = ordersRes.data ?? []
      if (orders.length === 0) {
        setPreview({ body: null, variables: {}, error: null, loading: false, noOrders: true })
        return
      }
      const orderId = orders[0].id
      const renderRes = await api.post<{ body: string; variables: Record<string, string> }>('/labels/render', {
        orderId,
        templateId,
      })
      if (!renderRes.data) throw new Error('No render result')
      setPreview({
        body: renderRes.data.body,
        variables: renderRes.data.variables ?? {},
        error: null,
        loading: false,
        noOrders: false,
      })
    } catch (err) {
      setPreview({
        body: null,
        variables: {},
        error: err instanceof ApiError ? err.message : 'Preview failed',
        loading: false,
        noOrders: false,
      })
    }
  }

  async function handleDownload() {
    if (!templateId) return
    setDownloading(true)
    setError(null)
    try {
      const result = await downloadLabelTemplate(templateId, form.name, form.driver)
      if (!result.ok) setError(result.error)
    } finally {
      setDownloading(false)
    }
  }

  async function handleClone() {
    if (!currentTemplate) return
    setCloning(true)
    setError(null)
    try {
      const payload = {
        name: `${currentTemplate.name} (Copy)`,
        description: currentTemplate.description ?? undefined,
        driver: currentTemplate.driver,
        paperSize: currentTemplate.paperSize ?? undefined,
        templateBody: currentTemplate.templateBody,
        variables: currentTemplate.variables,
      }
      const res = await api.post<LabelTemplateResponse>('/admin/label-templates', payload)
      if (!res.data) throw new Error('No data returned')
      // Open the clone for editing
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Clone failed')
    } finally {
      setCloning(false)
    }
  }

  const isSystem = currentTemplate?.isSystem ?? false
  const paperSizes = form.driver === 'dymo_client' ? DYMO_PAPER_SIZES : ZEBRA_PAPER_SIZES
  const bodyLabel = form.driver === 'dymo_client' ? 'DYMO XML' : 'ZPL'
  const title = isNew ? 'New Label Template' : `Label Template — ${form.name}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="760px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            {isSystem && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  marginBottom: '1rem',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}
                data-testid="system-template-notice"
              >
                This is a system-provided template. Edit a clone to customize.
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
              <FormField
                name="name"
                label="Name"
                required
                value={form.name}
                onChange={(v) => update('name', v)}
                disabled={isSystem}
              />
              <FormField name="driver" label="Driver" required>
                <select
                  style={input}
                  value={form.driver}
                  onChange={(e) => update('driver', e.target.value as TemplateForm['driver'])}
                  disabled={!isNew || isSystem}
                  data-testid="driver-select"
                >
                  <option value="dymo_client">DYMO</option>
                  <option value="zebra_browser_print">Zebra Browser Print</option>
                </select>
              </FormField>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField
                name="description"
                label="Description"
                value={form.description}
                onChange={(v) => update('description', v)}
                disabled={isSystem}
              />
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="paperSize" label="Paper Size">
                <input
                  id="paperSize"
                  style={input}
                  list="paper-size-options"
                  value={form.paperSize}
                  onChange={(e) => update('paperSize', e.target.value)}
                  disabled={isSystem}
                  placeholder="e.g. dymo_30252_address"
                  data-testid="paper-size"
                />
                <datalist id="paper-size-options">
                  {paperSizes.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </FormField>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500, color: colors.text, marginBottom: '0.375rem' }}>
                {bodyLabel} <span style={{ color: colors.danger }}>*</span>
              </div>
              <textarea
                style={{
                  ...input,
                  fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                  fontSize: '0.8125rem',
                  minHeight: '200px',
                  resize: 'vertical',
                  whiteSpace: 'pre',
                }}
                value={form.templateBody}
                onChange={(e) => update('templateBody', e.target.value)}
                disabled={isSystem}
                rows={20}
                data-testid="template-body"
              />
            </div>

            {/* Live Preview — synthetic sample data only, never live order/patient data (see utils/label-preview). */}
            <div style={{ marginTop: '0.5rem' }}>
              <button
                type="button"
                style={{ ...btnSecondary, padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}
                onClick={() => setShowLivePreview((v) => !v)}
                data-testid="toggle-live-preview-btn"
              >
                {showLivePreview ? 'Hide Live Preview' : 'Show Live Preview'}
              </button>
              {showLivePreview && (
                <div style={{ marginTop: '0.5rem' }}>
                  <LabelPreview driver={form.driver} body={form.templateBody} paperSize={form.paperSize} />
                </div>
              )}
            </div>

            {/* Variables editor */}
            <div
              style={{
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>Variables</h3>
                {!isSystem && (
                  <button type="button" style={btnSecondary} onClick={addVariable} data-testid="add-variable-btn">
                    Add Variable
                  </button>
                )}
              </div>
              {form.variables.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 1fr 1.5fr 0.5fr 1fr auto',
                    gap: '0.5rem',
                    marginBottom: '0.25rem',
                  }}
                >
                  {['Name', 'Type', 'Label', 'Req', 'Example', ''].map((h) => (
                    <span
                      key={h}
                      style={{
                        fontSize: '0.75rem',
                        color: colors.textMuted,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {h}
                    </span>
                  ))}
                </div>
              )}
              {form.variables.map((v, i) => (
                <VariableRow
                  key={i}
                  variable={v}
                  index={i}
                  onChange={isSystem ? () => {} : handleVariableChange}
                  onRemove={isSystem ? () => {} : handleVariableRemove}
                />
              ))}
              {form.variables.length === 0 && (
                <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No variables defined.</div>
              )}
            </div>

            {/* Render Preview */}
            <div
              style={{
                borderTop: `1px solid ${colors.border}`,
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: colors.text }}>
                  Render Preview
                </h3>
                <button
                  type="button"
                  style={btnSecondary}
                  onClick={handleRenderPreview}
                  disabled={isNew || preview.loading}
                  title={isNew ? 'Save the template first' : undefined}
                  data-testid="render-preview-btn"
                >
                  {preview.loading ? 'Rendering...' : 'Preview'}
                </button>
              </div>

              {preview.noOrders && (
                <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
                  Create an order first to preview this template.
                </div>
              )}
              {preview.error && (
                <div
                  style={{
                    color: colors.danger,
                    padding: '0.5rem 0.75rem',
                    background: colors.bgError,
                    border: `1px solid ${colors.borderError}`,
                    borderRadius: '3px',
                    fontSize: '0.875rem',
                  }}
                >
                  {preview.error}
                </div>
              )}
              {preview.body !== null && (
                <div>
                  <pre
                    style={{
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '4px',
                      padding: '0.75rem',
                      fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                      fontSize: '0.8125rem',
                      overflow: 'auto',
                      maxHeight: '300px',
                      whiteSpace: 'pre-wrap',
                    }}
                    data-testid="preview-body"
                  >
                    {preview.body}
                  </pre>
                  {Object.keys(preview.variables).length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div
                        style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.25rem', color: colors.text }}
                      >
                        Variables used:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8125rem', color: colors.textMuted }}>
                        {Object.entries(preview.variables).map(([k, v]) => (
                          <li key={k}>
                            <code>{k}</code> = {v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && isSystem && (
                <button
                  type="button"
                  style={{ ...btnSecondary, marginRight: 'auto' }}
                  onClick={handleClone}
                  disabled={cloning}
                  data-testid="clone-btn"
                >
                  {cloning ? 'Cloning...' : 'Clone to Custom Template'}
                </button>
              )}
              {!isNew && !isSystem && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                  data-testid="archive-btn"
                >
                  Archive
                </button>
              )}
              {!isNew && templateId && (
                <button
                  type="button"
                  onClick={() => void handleDownload()}
                  style={btnSecondary}
                  disabled={downloading}
                  data-testid="download-btn"
                >
                  {downloading ? 'Downloading...' : 'Download'}
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving || cloning}>
                {isSystem ? 'Close' : 'Cancel'}
              </button>
              {!isSystem && (
                <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving} data-testid="save-btn">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Label Template"
        message={`Archive "${form.name}"? It will no longer be selectable for new print jobs.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
